import { c as _c } from "react/compiler-runtime";
import { usePrefetchGetMarket, usePrefetchGetProposal } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { OptimizedImage } from '@/components/images/optimized-image';
import { ThumbWithOrigin } from '@/components/market/identity/identity-marks';
import type { MarketIdentityData } from '@/components/market/identity/market-identity';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { PROPOSAL_DETAIL_PREFETCH_FIELDS } from '@/lib/sdk/projections/launchpad';
import { FEED_CARD_HOVER_BACKGROUND, FEED_CARD_OUTLINE_INTERACTION, FEED_CARD_RESTING_OUTLINE } from '@/lib/ui/feed-card-chrome';
import { cn } from '@/lib/ui/utils';
const STATUS_TRADING = 2;
export interface NudgeMatchRowData extends MarketIdentityData {
  readonly pubkey: string;
  readonly title: string;
  readonly category: string | null;
  readonly status: number;
  readonly coverImageUrl: string;
}
export interface NudgeMatchRowProps {
  readonly match: NudgeMatchRowData;
}
export function NudgeMatchRow(t0) {
  const $ = _c(36);
  const {
    match
  } = t0;
  const t = useTranslations("launchpad.duplicateNudge");
  const isMarket = match.status === STATUS_TRADING;
  const href = isMarket ? `/markets/${match.pubkey}` : `/proposals/${match.pubkey}`;
  const pillKey = isMarket ? "pillMarket" : "pillProposal";
  let t1;
  if ($[0] !== match.category) {
    t1 = match.category !== null ? match.category.toLowerCase() : "";
    $[0] = match.category;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const category = t1;
  const prefetchMarket = usePrefetchGetMarket();
  const prefetchProposal = usePrefetchGetProposal();
  let t2;
  if ($[2] !== isMarket || $[3] !== match.pubkey || $[4] !== prefetchMarket || $[5] !== prefetchProposal) {
    t2 = function handlePrefetch() {
      if (isMarket) {
        prefetchMarket({
          pubkey: match.pubkey
        });
      } else {
        prefetchProposal({
          pubkey: match.pubkey,
          fields: PROPOSAL_DETAIL_PREFETCH_FIELDS
        });
      }
    };
    $[2] = isMarket;
    $[3] = match.pubkey;
    $[4] = prefetchMarket;
    $[5] = prefetchProposal;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handlePrefetch = t2;
  const intent = useIntentRoutePrefetch(undefined, handlePrefetch);
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("relative flex items-center gap-3 rounded-panel-inset bg-background-primary px-3 py-2", FEED_CARD_HOVER_BACKGROUND, FEED_CARD_RESTING_OUTLINE, FEED_CARD_OUTLINE_INTERACTION);
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== href || $[9] !== intent || $[10] !== match.title) {
    t4 = <Link href={href} target="_blank" rel="noopener noreferrer" prefetch={false} {...intent} aria-label={match.title} className="absolute inset-0 rounded-[inherit] focus-visible:outline focus-visible:outline-2 focus-visible:outline-border-primary" />;
    $[8] = href;
    $[9] = intent;
    $[10] = match.title;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = match.coverImageUrl.length > 0 ? match.coverImageUrl : null;
  let t6;
  if ($[12] !== category || $[13] !== match.pubkey || $[14] !== t5) {
    t6 = <OptimizedImage variant="thumbnail" entity={{
      kind: "market",
      pubkey: match.pubkey,
      category,
      coverImageUrl: t5
    }} alt="" size="size-10" rounded="rounded-control" preview={false} />;
    $[12] = category;
    $[13] = match.pubkey;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== match.marketType || $[17] !== t6) {
    t7 = <ThumbWithOrigin marketType={match.marketType}>{t6}</ThumbWithOrigin>;
    $[16] = match.marketType;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== match.title) {
    t8 = <div className="line-clamp-1 text-caption font-semibold text-foreground-primary">{match.title}</div>;
    $[19] = match.title;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = isMarket ? "bg-feedback-success-surface text-feedback-success-content" : "border text-foreground-secondary";
  let t10;
  if ($[21] !== t9) {
    t10 = cn("inline-flex items-center rounded-pill px-2 py-0.5 text-micro font-medium", t9);
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== pillKey || $[24] !== t) {
    t11 = t(pillKey);
    $[23] = pillKey;
    $[24] = t;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== t10 || $[27] !== t11) {
    t12 = <div className="mt-0.5 flex items-center gap-2"><span className={t10}>{t11}</span></div>;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t12 || $[30] !== t8) {
    t13 = <div className="min-w-0 flex-1">{t8}{t12}</div>;
    $[29] = t12;
    $[30] = t8;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t13 || $[33] !== t4 || $[34] !== t7) {
    t14 = <div className={t3}>{t4}{t7}{t13}</div>;
    $[32] = t13;
    $[33] = t4;
    $[34] = t7;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
}